import React, { Suspense } from "react";
import { Analytics } from "@vercel/analytics/react";
import { AppToaster } from "@/components/app/AppToaster";
import { AppLogo } from "@/components/app/brand/AppLogo";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { ShellProvider } from "@/components/app/shell/ShellProvider";
import { ResponsiveProvider } from "@/contexts/ResponsiveContext";
import ScrollToTop from "./components/new-landing/ScrollToTop";
import FaviconManager from "./components/FaviconManager";
import MarketingLayout from "@/layouts/MarketingLayout";

// Static Imports for SSG/Landing Pages
import Index from "./pages/IndexPage";
import Auth from "./pages/Auth";

// Lazy Load Pages
const Player = React.lazy(() => import("./pages/new-landing/Player"));
const Coach = React.lazy(() => import("./pages/new-landing/Coach"));
const ClubOwner = React.lazy(() => import("./pages/new-landing/ClubOwner"));
const Parent = React.lazy(() => import("./pages/new-landing/Parent"));
const VenueOwner = React.lazy(() => import("./pages/new-landing/VenueOwner"));
const TournamentOrganizer = React.lazy(
  () => import("./pages/new-landing/TournamentOrganizer"),
);
const Fan = React.lazy(() => import("./pages/new-landing/Fan"));
const Clubs = React.lazy(() => import("./pages/new-landing/Clubs"));
const ClubDirectoryDetail = React.lazy(
  () => import("./pages/new-landing/ClubDirectoryDetail"),
);
const BookingDemo = React.lazy(() => import("./pages/new-landing/BookingDemo"));
const About = React.lazy(() => import("./pages/new-landing/About"));
const Contact = React.lazy(() => import("./pages/new-landing/Contact"));
const Terms = React.lazy(() => import("./pages/new-landing/Terms"));
const Privacy = React.lazy(() => import("./pages/new-landing/Privacy"));
const FAQ = React.lazy(() => import("./pages/new-landing/FAQ"));

const EnhancedRegister = React.lazy(() => import("./pages/EnhancedRegister"));
const ParentSchedule = React.lazy(() => import("./pages/parent/Schedule"));
const ParentPerformance = React.lazy(
  () => import("./pages/parent/Performance"),
);
const ParentProgramFees = React.lazy(
  () => import("./pages/parent/ProgramFees"),
);
const WhatsAppSettings = React.lazy(
  () => import("./pages/super/WhatsAppSettings"),
);
const Feedback = React.lazy(() => import("./pages/Feedback"));
const CoachSchedule = React.lazy(() => import("./pages/coach/Schedule"));
const TrainingAttendanceDetail = React.lazy(
  () => import("./pages/coach/TrainingAttendanceDetail"),
);
const ClubPosts = React.lazy(() => import("./pages/owner/ClubPosts"));
const ClubPublicFeed = React.lazy(() => import("./pages/club/ClubFeed"));
const ClubPostDetail = React.lazy(() => import("./pages/club/ClubPostDetail"));
const Tournaments = React.lazy(() => import("./pages/organizer/Tournaments"));
const TournamentDetails = React.lazy(
  () => import("./pages/organizer/TournamentDetails"),
);
const TournamentRegistrations = React.lazy(
  () => import("./pages/organizer/TournamentRegistrations"),
);
const CreateTeam = React.lazy(() => import("./pages/team/CreateTeam"));
const MyTeams = React.lazy(() => import("./pages/team/MyTeams"));
const TeamDetail = React.lazy(() => import("./pages/team/TeamDetail"));
const AdminFeedback = React.lazy(() => import("./pages/admin/Feedback"));
const TournamentView = React.lazy(
  () => import("./pages/tournament/TournamentView"),
);
const TournamentRegistration = React.lazy(
  () => import("./pages/tournament/TournamentRegistration"),
);
const MyTournaments = React.lazy(
  () => import("./pages/tournament/MyTournaments"),
);

// Add missing lazy imports
const LoadingDemo = import.meta.env.DEV
  ? React.lazy(() => import("./components/LoadingDemo"))
  : null;
const UpdatePassword = React.lazy(() => import("./pages/UpdatePassword"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const PendingApproval = React.lazy(() => import("./pages/PendingApproval"));
const ComparePlans = React.lazy(() => import("./pages/ComparePlans"));
const ContentPage = React.lazy(() => import("./pages/content/ContentPage"));
const SubscriptionCheckout = React.lazy(
  () => import("./pages/SubscriptionCheckout"),
);
const SuccessPage = React.lazy(() => import("./pages/SuccessPage"));
const CancelPage = React.lazy(() => import("./pages/CancelPage"));
const Billing = React.lazy(() => import("./pages/Billing"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Explore = React.lazy(() => import("./pages/explore/Explore"));
const Favorites = React.lazy(() => import("./pages/favourite/Favourite"));
const ClubView = React.lazy(() => import("./pages/club/ClubView"));
const PlayerView = React.lazy(() => import("./pages/player/PlayerView"));
const ProgramApply = React.lazy(() => import("./pages/program/Apply"));
const PlayerApply = React.lazy(() => import("./pages/player/Apply"));
const ParentApply = React.lazy(() => import("./pages/parent/Apply"));
const CoachView = React.lazy(() => import("./pages/coach/CoachView"));

// Newly added missing components
const FanSubscribe = React.lazy(() => import("./pages/fan/Subscribe"));
const FanEvents = React.lazy(() => import("./pages/fan/Events"));
const FanSettings = React.lazy(() => import("./pages/fan/Settings"));
const FanNotifications = React.lazy(() => import("./pages/fan/Notifications"));
const NotificationSettings = React.lazy(
  () => import("./pages/NotificationSettings"),
);
const PlayerDashboard = React.lazy(() => import("./pages/player/Dashboard"));
const PlayerLeaderboard = React.lazy(
  () => import("./pages/player/Leaderboard"),
);
const ConfirmEmail = React.lazy(() => import("./pages/batch-a/ConfirmEmail"));
const OnboardingPage = React.lazy(() => import("./pages/batch-a/OnboardingPage"));
const PrivacyCenter = React.lazy(() => import("./pages/batch-a/PrivacyCenter"));
const PlayerDevelopment = React.lazy(() => import("./pages/batch-a/PlayerDevelopment"));
const SearchPage = React.lazy(() => import("./pages/batch-a/SearchPage"));
const ScoutTools = React.lazy(() => import("./pages/batch-a/ScoutTools"));
const MarketplaceHeld = React.lazy(() => import("./pages/batch-a/MarketplaceHeld"));
const TeamOps = React.lazy(() => import("./pages/batch-a/TeamOps"));
const SafeguardingAdmin = React.lazy(() => import("./pages/batch-a/SafeguardingAdmin"));
const PdpaAdmin = React.lazy(() => import("./pages/batch-a/PdpaAdmin"));
const OpsKpis = React.lazy(() => import("./pages/batch-a/OpsKpis"));
const PlayerSchedule = React.lazy(() => import("./pages/player/Schedule"));
const PlayerIndividualTraining = React.lazy(() => import("./pages/player/IndividualTraining"));
const PlayerIndividualTrainingDetail = React.lazy(() => import("./pages/player/IndividualTrainingDetail"));
const PlayerMatches = React.lazy(() => import("./pages/player/Matches"));
const PlayerApplications = React.lazy(
  () => import("./pages/player/Applications"),
);
const PlayerTrialInvitations = React.lazy(
  () => import("./pages/player/TrialInvitations"),
);
const PlayerTrialInvitationDetail = React.lazy(
  () => import("./pages/player/TrialInvitationDetail"),
);
const MyPlayerProfile = React.lazy(
  () => import("./pages/player/MyPlayerProfile"),
);
const MyClubs = React.lazy(() => import("./pages/player/MyClubs"));
const PlayerBilling = React.lazy(() => import("./pages/player/Billing"));
const ProgramFees = React.lazy(() => import("./pages/player/ProgramFees"));
const OwnPlayerPerformance = React.lazy(
  () => import("./pages/player/Performance"),
);
const CoachDashboard = React.lazy(() => import("./pages/coach/Dashboard"));
const MyCoachProfile = React.lazy(() => import("./pages/coach/MyCoachProfile"));
const CoachTrialParticipants = React.lazy(
  () => import("./pages/coach/TrialParticipants"),
);
const TrainingManagement = React.lazy(
  () => import("./pages/coach/TrainingManagement"),
);
const SquadManagement = React.lazy(
  () => import("./pages/coach/SquadManagement"),
);
const OwnerDashboard = React.lazy(() => import("./pages/owner/Dashboard"));
const MatchView = React.lazy(() => import("./pages/match/MatchView"));
const ProgramDetail = React.lazy(() => import("./pages/program/ProgramDetail"));
const ParentApplications = React.lazy(
  () => import("./pages/parent/Applications"),
);
const ParentIndividualTraining = React.lazy(
  () => import("./pages/parent/IndividualTraining"),
);
const ParentIndividualTrainingDetail = React.lazy(
  () => import("./pages/parent/IndividualTrainingDetail"),
);

// Club Owner
const MyClub = React.lazy(() => import("./pages/club/MyClub"));
const OwnerPrograms = React.lazy(() => import("./pages/owner/Programs"));
const ClubPlayers = React.lazy(() => import("./pages/owner/ClubPlayers"));
const OwnerRoster = React.lazy(() => import("./pages/owner/Roster"));
const OwnerMatchOps = React.lazy(() => import("./pages/owner/MatchOps"));
const OwnerFinance = React.lazy(() => import("./pages/owner/Finance"));
const PlayerPayments = React.lazy(() => import("./pages/owner/PlayerPayments"));
const PlayerPerformance = React.lazy(
  () => import("./pages/club/PlayerPerformance"),
);
const ClubSchedule = React.lazy(() => import("./pages/club/Schedule"));
const TacticalPlanList = React.lazy(
  () => import("./pages/club/TacticalPlanList"),
);
const TacticalPlanDetail = React.lazy(
  () => import("./pages/club/TacticalPlanDetail"),
);
const DailyAttendance = React.lazy(
  () => import("./pages/club/DailyAttendance"),
);
const ClubIndividualTrainingPlan = React.lazy(
  () => import("./pages/club/IndividualTrainingPlanList"),
);
const ClubIndividualTrainingPlanDetail = React.lazy(
  () => import("./pages/club/IndividualTrainingPlanDetail"),
);
const OwnerStaff = React.lazy(() => import("./pages/owner/Staff"));
const OwnerApplications = React.lazy(
  () => import("./pages/owner/Applications"),
);
const TrialInvitations = React.lazy(
  () => import("./pages/owner/TrialInvitations"),
);
const OwnerReports = React.lazy(() => import("./pages/owner/Reports"));
const ScouterMarketplace = React.lazy(
  () => import("./pages/owner/ScouterMarketplace"),
);
const ScouterProfile = React.lazy(() => import("./pages/owner/ScouterProfile"));
const ScouterContracts = React.lazy(
  () => import("./pages/owner/ScouterContracts"),
);
const ScouterMatching = React.lazy(
  () => import("./pages/owner/ScouterMatching"),
);
const OwnerScouterReports = React.lazy(
  () => import("./pages/owner/OwnerScouterReports"),
);
const OwnerScoutingReports = React.lazy(
  () => import("./pages/owner/ScoutingReports"),
);

// Scouter
const ScouterDashboard = React.lazy(() => import("./pages/scouter/Dashboard"));
const ScouterProfileSettings = React.lazy(
  () => import("./pages/scouter/ProfileSettings"),
);
const ScouterContractsPage = React.lazy(
  () => import("./pages/scouter/Contracts"),
);
const ScouterSubmitReport = React.lazy(
  () => import("./pages/scouter/SubmitReport"),
);
const ScouterPerformance = React.lazy(
  () => import("./pages/scouter/Performance"),
);

// Scout
const ScoutDashboard = React.lazy(() => import("./pages/scout/Dashboard"));
const ScoutTalentSearch = React.lazy(
  () => import("./pages/scout/TalentSearch"),
);
const ScoutReports = React.lazy(() => import("./pages/scout/Reports"));
const ScoutRecommendations = React.lazy(
  () => import("./pages/scout/Recommendations"),
);
const TrialTracking = React.lazy(() => import("./pages/scout/TrialTracking"));

// Coach
const CoachRecommendations = React.lazy(
  () => import("./pages/coach/Recommendations"),
);

// Referee
const RefereeDashboard = React.lazy(() => import("./pages/referee/Dashboard"));
const RefereeAssignments = React.lazy(
  () => import("./pages/referee/Assignments"),
);
const RefereeResultEntry = React.lazy(
  () => import("./pages/referee/ResultEntry"),
);
const RefereeProfile = React.lazy(() => import("./pages/referee/Profile"));
const RefereeOpenGames = React.lazy(() => import("./pages/referee/OpenGames"));
const RefereeDiscipline = React.lazy(() => import("./pages/referee/Discipline"));

// Medical
const MedicalDashboard = React.lazy(() => import("./pages/medical/Dashboard"));
const MedicalInjuries = React.lazy(() => import("./pages/medical/Injuries"));
const MedicalClearances = React.lazy(() => import("./pages/medical/Clearances"));
const InjuryAnalytics = React.lazy(() => import("./pages/medical/InjuryAnalytics"));
const StaffAssignments = React.lazy(() => import("./pages/welfare/StaffAssignments"));
const LiveMatch = React.lazy(() => import("./pages/match/LiveMatch"));
const MatchDesk = React.lazy(() => import("./pages/match/MatchDesk"));
const PublicHub = React.lazy(() => import("./pages/tournament/PublicHub"));
const TournamentOps = React.lazy(() => import("./pages/organizer/TournamentOps"));
const MedicWelfare = React.lazy(() => import("./pages/welfare/MedicWelfare"));
const CoachWelfare = React.lazy(() => import("./pages/welfare/CoachWelfare"));
const PlayerWelfare = React.lazy(() => import("./pages/welfare/PlayerWelfare"));
const ParentWelfare = React.lazy(() => import("./pages/welfare/ParentWelfare"));
const ParentHousehold = React.lazy(() => import("./pages/parent/Household"));
const EventCheckIn = React.lazy(() => import("./pages/organizer/EventCheckIn"));

// Venue
const VenueDashboard = React.lazy(() => import("./pages/venue/Dashboard"));
const MyVenue = React.lazy(() => import("./pages/venue/MyVenue"));
const VenueDetails = React.lazy(() => import("./pages/venue/VenueDetails"));
const VenueBookings = React.lazy(() => import("./pages/venue/Bookings"));
const VenuePricing = React.lazy(() => import("./pages/venue/Pricing"));
const VenueInsights = React.lazy(() => import("./pages/venue/Insights"));

// Organizer
const OrganizerDashboard = React.lazy(
  () => import("./pages/organizer/Dashboard"),
);
const OrganizerBrackets = React.lazy(
  () => import("./pages/organizer/Brackets"),
);
const OrganizerSchedule = React.lazy(
  () => import("./pages/organizer/Schedule"),
);

// Team Manager
const TeamManagerDashboard = React.lazy(
  () => import("./pages/team-manager/Dashboard"),
);
const TeamManagerClubPage = React.lazy(
  () => import("./pages/team-manager/ClubPage"),
);
const TeamManagerMedia = React.lazy(
  () => import("./pages/team-manager/MediaManagement"),
);
const TeamManagerProductivity = React.lazy(
  () => import("./pages/team-manager/Productivity"),
);
const TeamManagerMatchCoordination = React.lazy(
  () => import("./pages/team-manager/MatchCoordination"),
);
const TeamManagerReports = React.lazy(
  () => import("./pages/team-manager/Reports"),
);

// Media Manager
const PostsManagement = React.lazy(
  () => import("./pages/media-manager/PostsManagement"),
);
const MediaManagerAnalytics = React.lazy(
  () => import("./pages/media-manager/Analytics"),
);

// Parent
const ParentChildren = React.lazy(
  () => import("./pages/parent/ParentChildren"),
);
const ChildrenPlayerProfile = React.lazy(
  () => import("./pages/parent/ChildrenPlayerProfile"),
);

// Admin
const AdminDashboard = React.lazy(() => import("./pages/admin/Dashboard"));
const ApprovalCenter = React.lazy(() => import("./pages/admin/ApprovalCenter"));
const NationalIDVerification = React.lazy(
  () => import("./pages/admin/NationalIDVerification"),
);
const UserManagementV2 = React.lazy(
  () => import("./pages/admin/UserManagementV2"),
);
const ParentAccountManagement = React.lazy(
  () => import("./pages/admin/ParentAccountManagement"),
);
const Moderation = React.lazy(() => import("./pages/admin/Moderation"));
const SystemReports = React.lazy(() => import("./pages/admin/SystemReports"));

// Super Admin
const SuperAdminDashboard = React.lazy(() => import("./pages/super/Dashboard"));
const AdminAnnouncement = React.lazy(
  () => import("./pages/admin/Announcement"),
);
const SubscriptionDashboard = React.lazy(
  () => import("./pages/super/subscriptions/Dashboard"),
);
const SubscriptionPlans = React.lazy(
  () => import("./pages/super/subscriptions/Plans"),
);
const ActiveSubscriptions = React.lazy(
  () => import("./pages/super/subscriptions/ActiveSubscriptions"),
);
const BillingTransactions = React.lazy(
  () => import("./pages/super/subscriptions/Transactions"),
);
const PromoCodes = React.lazy(
  () => import("./pages/super/subscriptions/PromoCodes"),
);
const MonetizationSettings = React.lazy(
  () => import("./pages/super/subscriptions/Settings"),
);
const UsageAnalytics = React.lazy(
  () => import("./pages/super/subscriptions/UsageAnalytics"),
);
const PageValidator = import.meta.env.DEV
  ? React.lazy(() => import("./pages/super/PageValidator"))
  : null;
const ContentManager = React.lazy(() => import("./pages/super/ContentManager"));
const FooterManager = React.lazy(() => import("./pages/super/FooterManager"));
const EmailTemplates = React.lazy(
  () => import("./pages/super/content/EmailTemplates"),
);
const TranslationsManager = React.lazy(
  () => import("./pages/super/content/Translations"),
);
const FeatureFlags = React.lazy(() => import("./pages/super/FeatureFlags"));
const Monitoring = React.lazy(() => import("./pages/super/Monitoring"));
const SystemSettings = React.lazy(() => import("./pages/super/SystemSettings"));
const AdminManagement = React.lazy(
  () => import("./pages/super/AdminManagement"),
);
const RolesPermissions = React.lazy(
  () => import("./pages/super/RolesPermissions"),
);
const AIControl = React.lazy(() => import("./pages/super/AIControl"));
const AuditLogs = React.lazy(() => import("./pages/super/AuditLogs"));
const Financial = React.lazy(() => import("./pages/super/Financial"));
const IntegrationTest = import.meta.env.DEV
  ? React.lazy(() => import("./pages/super/IntegrationTest"))
  : null;
const BatchCOps = React.lazy(() => import("./pages/batch-c/BatchCOps"));
const PublicSearch = React.lazy(() =>
  import("./pages/batch-c/PublicDiscovery").then((mod) => ({ default: mod.PublicSearch })),
);
const PublicVenues = React.lazy(() =>
  import("./pages/batch-c/PublicDiscovery").then((mod) => ({ default: mod.PublicVenues })),
);
const PublicFixture = React.lazy(() =>
  import("./pages/batch-c/PublicDiscovery").then((mod) => ({ default: mod.PublicFixture })),
);
const PublicTournament = React.lazy(() =>
  import("./pages/batch-c/PublicDiscovery").then((mod) => ({ default: mod.PublicTournament })),
);
const IntegrationsConfig = React.lazy(
  () => import("./pages/super/IntegrationsConfig"),
);
const RefundCenter = React.lazy(() => import("./pages/super/RefundCenter"));

// General
const Messages = React.lazy(() => import("./pages/Messages"));
const Profile = React.lazy(() => import("./pages/Profile"));
const RoleRequest = React.lazy(() => import("./pages/RoleRequest"));
const Social = React.lazy(() => import("./pages/Social"));
const Jobs = React.lazy(() => import("./pages/Jobs"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const TacticalBoardPage = React.lazy(() => import("./pages/tactical-board"));
const AnnouncementPage = React.lazy(() => import("./pages/Announcement"));
const BlogList = React.lazy(() => import("./pages/blog/BlogList"));
const BlogPost = React.lazy(() => import("./pages/blog/BlogPost"));
const BlogManager = React.lazy(() => import("./pages/super/blog/BlogManager"));
const EditPost = React.lazy(() => import("./pages/super/blog/EditPost"));

// Helper for loading
const Loading = () => (
  <div className="flex h-screen w-full flex-col items-center justify-center gap-4" aria-busy="true">
    <AppLogo size={56} title="FanJago" />
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 20_000,
    },
  },
});

// Responsive App Wrapper Component
const ResponsiveAppWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="min-h-screen w-full bg-background">
      {/* Responsive Meta Tags */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes"
      />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Responsive Font Scaling */}
      <style>{`
        @media (max-width: 640px) {
          html { font-size: 14px; }
        }
        @media (min-width: 641px) and (max-width: 1024px) {
          html { font-size: 15px; }
        }
        @media (min-width: 1025px) {
          html { font-size: 16px; }
        }
        
        /* Smooth scrolling for all devices */
        html {
          scroll-behavior: smooth;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        /* Touch-friendly improvements */
        @media (hover: none) and (pointer: coarse) {
          button, a, input, select, textarea {
            min-height: 44px;
            min-width: 44px;
          }
        }
        
        /* Responsive container improvements */
        .responsive-container {
          width: 100%;
          margin-left: auto;
          margin-right: auto;
          padding-left: 1rem;
          padding-right: 1rem;
        }
        
        @media (min-width: 640px) {
          .responsive-container {
            max-width: 640px;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        
        @media (min-width: 768px) {
          .responsive-container {
            max-width: 768px;
          }
        }
        
        @media (min-width: 1024px) {
          .responsive-container {
            max-width: 1024px;
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }
        
        @media (min-width: 1280px) {
          .responsive-container {
            max-width: 1280px;
          }
        }
        
        @media (min-width: 1536px) {
          .responsive-container {
            max-width: 1536px;
            padding-left: 3rem;
            padding-right: 3rem;
          }
        }
      `}</style>

      {children}
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <ResponsiveProvider>
          <TooltipProvider>
            <AppToaster />
            {/* <PWAUpdatePrompt /> */}
            <FaviconManager />
            <Analytics />
            <ResponsiveAppWrapper>
              <ScrollToTop />
              <Suspense fallback={<Loading />}>
                <ShellProvider>
                <Routes>
                  {/* <marketing-routes> */}
                  <Route element={<MarketingLayout />}>
                    <Route path="/" element={<Index />} />
                    {/* New Landing */}
                    <Route path="/player" element={<Player />} />
                    <Route path="/coach" element={<Coach />} />
                    <Route path="/club-owner" element={<ClubOwner />} />
                    <Route path="/parent" element={<Parent />} />
                    <Route path="/venue-owner" element={<VenueOwner />} />
                    <Route
                      path="/tournament-organizer"
                      element={<TournamentOrganizer />}
                    />
                    <Route path="/fan" element={<Fan />} />
                    <Route path="/clubs" element={<Clubs />} />
                    <Route
                      path="/clubs/:slug"
                      element={<ClubDirectoryDetail />}
                    />
                    <Route path="/book-demo" element={<BookingDemo />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/feedback" element={<Feedback />} />
                    <Route
                      path="/announcement"
                      element={<AnnouncementPage />}
                    />
                    <Route path="/blog" element={<BlogList />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                  </Route>
                  {/* </marketing-routes> */}
                  {import.meta.env.DEV && LoadingDemo ? (
                    <Route path="/loading-demo" element={<LoadingDemo />} />
                  ) : null}
                  <Route path="/search" element={<PublicSearch />} />
                  <Route path="/venues" element={<PublicVenues />} />
                  <Route path="/public/fixtures/:matchId" element={<PublicFixture />} />
                  <Route path="/public/tournaments/:tournamentId" element={<PublicTournament />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/forgot" element={<ForgotPassword />} />
                  <Route path="/confirm-email" element={<ConfirmEmail />} />
                  <Route
                    path="/auth/update-password"
                    element={<UpdatePassword />}
                  />
                  <Route path="/register" element={<EnhancedRegister />} />
                  <Route path="/live/match/:matchId" element={<LiveMatch />} />
                  <Route path="/t/:tournamentId" element={<PublicHub />} />
                  <Route
                    path="/pending-approval"
                    element={<PendingApproval />}
                  />
                  <Route path="/compare-plans" element={<ComparePlans />} />
                  <Route path="/careers" element={<ContentPage />} />
                  <Route path="/support" element={<ContentPage />} />
                  <Route path="/refunds" element={<ContentPage />} />
                  <Route path="/cookies" element={<ContentPage />} />
                  <Route path="/platform/explore" element={<ContentPage />} />
                  <Route
                    path="/platform/club-management"
                    element={<ContentPage />}
                  />
                  <Route path="/platform/scouting" element={<ContentPage />} />
                  <Route path="/platform/organizer" element={<ContentPage />} />
                  <Route
                    path="/subscription/checkout/:planId"
                    element={
                      <ProtectedRoute>
                        <SubscriptionCheckout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/success"
                    element={
                      <ProtectedRoute>
                        <SuccessPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/cancel"
                    element={
                      <ProtectedRoute>
                        <CancelPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Billing />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* All User */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/explore"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Explore />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/favorites"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Favorites />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tactical-board"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TacticalBoardPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/:clubId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ClubView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/:playerId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PlayerView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-team"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MyTeams />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-tournaments"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MyTournaments />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/program/apply"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProgramApply />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/apply"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PlayerApply />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tournament/:tournamentId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TournamentView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tournament/:tournamentId/register"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TournamentRegistration />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/apply"
                    element={
                      <ProtectedRoute allowedRoles={["parent", "player", "fan"]}>
                        <Layout>
                          <ParentApply />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/:coachId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CoachView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/match/:matchId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MatchView />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/match/:matchId/desk"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "coach",
                          "team_manager",
                          "referee",
                          "club_owner",
                          "tournament_organizer",
                          "club_admin",
                        ]}
                      >
                        <Layout>
                          <MatchDesk />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/feed/:clubId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ClubPublicFeed />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/post/:postId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ClubPostDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/program/:programId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "coach",
                          "club_owner",
                          "club_finance",
                          "club_manager",
                          "club_admin",
                          "scout",
                          "admin",
                          "super_admin",
                        ]}
                      >
                        <Layout>
                          <ProgramDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/training/:trainingId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "coach",
                          "club_owner",
                          "club_manager",
                          "club_admin",
                          "club_finance",
                          "admin",
                          "super_admin",
                          "player",
                          "parent",
                        ]}
                      >
                        <Layout>
                          <TrainingAttendanceDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/fan/subscribe"
                    element={
                      <ProtectedRoute allowedRoles={["fan"]}>
                        <Layout>
                          <FanSubscribe />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fan/events"
                    element={
                      <ProtectedRoute allowedRoles={["fan"]}>
                        <Layout>
                          <FanEvents />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/fan/settings" element={<Navigate to="/settings" replace />} />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <FanSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/fan/notifications"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <FanNotifications />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notification-settings"
                    element={
                      <ProtectedRoute>
                        <NotificationSettings />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/player/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/passport"
                    element={<Navigate to="/profile?tab=passport" replace />}
                  />
                  <Route
                    path="/player/stats"
                    element={<Navigate to="/profile?tab=stats" replace />}
                  />
                  <Route
                    path="/player/leaderboard"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerLeaderboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/achievements"
                    element={
                      <Navigate to="/profile?tab=achievements" replace />
                    }
                  />
                  <Route
                    path="/player/wallet"
                    element={<Navigate to="/player/dashboard" replace />}
                  />
                  <Route
                    path="/player/health"
                    element={<Navigate to="/player/welfare" replace />}
                  />
                  <Route
                    path="/player/development"
                    element={
                      <ProtectedRoute allowedRoles={["player", "coach", "parent"]}>
                        <Layout>
                          <PlayerDevelopment />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/welfare"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerWelfare />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/schedule"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerSchedule />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/performance"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <OwnPlayerPerformance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/individual-training"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerIndividualTraining />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/individual-training/:itpId"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerIndividualTrainingDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/matches"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerMatches />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/applications"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerApplications />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/applications"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentApplications />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/performance"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentPerformance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/individual-training"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentIndividualTraining />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/individual-training/:itpId"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentIndividualTrainingDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/trial-invitations"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerTrialInvitations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/trial-invitations/:trialId"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerTrialInvitationDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/portfolio"
                    element={<Navigate to="/profile?tab=portfolio" replace />}
                  />
                  <Route
                    path="/player/my-profile"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <MyPlayerProfile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/club"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <MyClubs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/billing"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <PlayerBilling />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/player/program-fee"
                    element={
                      <ProtectedRoute allowedRoles={["player"]}>
                        <Layout>
                          <ProgramFees />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/coach/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <CoachDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/my-profile"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <MyCoachProfile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/schedule"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <CoachSchedule />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/trial-participants"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <CoachTrialParticipants />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/training-management"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <TrainingManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/squad-management"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <SquadManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/welfare"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <CoachWelfare />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Club Owner Routes - Primary paths */}
                  <Route
                    path="/owner/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <OwnerDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/my-club"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <MyClub />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/programs"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerPrograms />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/posting"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <ClubPosts />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/players"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                          "coach",
                          "team_manager",
                        ]}
                      >
                        <Layout>
                          <ClubPlayers />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/roster"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                          "coach",
                          "team_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerRoster />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/match-ops"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerMatchOps />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/finance"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerFinance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/player-payments"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <PlayerPayments />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/player-performance"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                          "coach",
                        ]}
                      >
                        <Layout>
                          <PlayerPerformance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/schedule"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                          "coach",
                        ]}
                      >
                        <Layout>
                          <ClubSchedule />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/tactical-plan/new"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach",
                        ]}
                      >
                        <Layout>
                          <TacticalPlanDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/tactical-plan/:tacticalPlanId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach",
                        ]}
                      >
                        <Layout>
                          <TacticalPlanDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/tactical-plan"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach",
                        ]}
                      >
                        <Layout>
                          <TacticalPlanList />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/individual-training-plans"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach"
                        ]}
                      >
                        <Layout>
                          <ClubIndividualTrainingPlan />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/individual-training-plans/new"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach"
                        ]}
                      >
                        <Layout>
                          <ClubIndividualTrainingPlanDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/individual-training-plans/:itpId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach"
                        ]}
                      >
                        <Layout>
                          <ClubIndividualTrainingPlanDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/attendance/:clubId/:date"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "coach",
                          "player",
                          "parent",
                        ]}
                      >
                        <Layout>
                          <DailyAttendance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/staff"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerStaff />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/applications"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <OwnerApplications />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/trial-invitations"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "club_owner",
                          "club_admin",
                          "club_finance",
                          "club_manager",
                        ]}
                      >
                        <Layout>
                          <TrialInvitations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/reports"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner", "club_admin", "club_finance"]}>
                        <Layout>
                          <OwnerReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/scouter-marketplace"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <MarketplaceHeld />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/scouter/:id"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <ScouterProfile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/scouter-contracts"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <ScouterContracts />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/scouter-matching"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <ScouterMatching />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/club/scouter-reports"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <OwnerScouterReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Scouter Routes */}
                  <Route
                    path="/scouter/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["scouter"]}>
                        <Layout>
                          <ScouterDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scouter/profile-settings"
                    element={
                      <ProtectedRoute allowedRoles={["scouter"]}>
                        <Layout>
                          <ScouterProfileSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scouter/contracts"
                    element={
                      <ProtectedRoute allowedRoles={["scouter"]}>
                        <Layout>
                          <ScouterContractsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scouter/submit-report"
                    element={
                      <ProtectedRoute allowedRoles={["scouter"]}>
                        <Layout>
                          <ScouterSubmitReport />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scouter/performance"
                    element={
                      <ProtectedRoute allowedRoles={["scouter"]}>
                        <Layout>
                          <ScouterPerformance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Legacy /club_owner and /club-owner URLs redirect to the current club routes. */}
                  <Route
                    path="/club_owner/dashboard"
                    element={<Navigate to="/owner/dashboard" replace />}
                  />
                  <Route
                    path="/club_owner/programs"
                    element={<Navigate to="/club/programs" replace />}
                  />
                  <Route
                    path="/club_owner/roster"
                    element={<Navigate to="/club/roster" replace />}
                  />
                  <Route
                    path="/club_owner/match-ops"
                    element={<Navigate to="/club/match-ops" replace />}
                  />
                  <Route
                    path="/club_owner/finance"
                    element={<Navigate to="/club/finance" replace />}
                  />
                  <Route
                    path="/club_owner/staff"
                    element={<Navigate to="/club/staff" replace />}
                  />
                  <Route
                    path="/club-owner/dashboard"
                    element={<Navigate to="/owner/dashboard" replace />}
                  />
                  <Route
                    path="/club-owner/programs"
                    element={<Navigate to="/club/programs" replace />}
                  />
                  <Route
                    path="/club-owner/roster"
                    element={<Navigate to="/club/roster" replace />}
                  />
                  <Route
                    path="/club-owner/match-ops"
                    element={<Navigate to="/club/match-ops" replace />}
                  />
                  <Route
                    path="/club-owner/finance"
                    element={<Navigate to="/club/finance" replace />}
                  />
                  <Route
                    path="/club-owner/staff"
                    element={<Navigate to="/club/staff" replace />}
                  />
                  <Route
                    path="/club_owner/applications"
                    element={<Navigate to="/club/applications" replace />}
                  />
                  <Route
                    path="/club-owner/applications"
                    element={<Navigate to="/club/applications" replace />}
                  />
                  <Route
                    path="/club_owner/reports"
                    element={<Navigate to="/club/reports" replace />}
                  />
                  <Route
                    path="/club-owner/reports"
                    element={<Navigate to="/club/reports" replace />}
                  />
                  <Route
                    path="/scout/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["scout"]}>
                        <Layout>
                          <ScoutDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/vetting"
                    element={
                      <ProtectedRoute allowedRoles={["scout", "scouter", "admin", "super_admin"]}>
                        <Layout>
                          <ScoutTools />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/shortlists"
                    element={
                      <ProtectedRoute allowedRoles={["scout", "scouter"]}>
                        <Layout>
                          <ScoutTools />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/talent-search"
                    element={
                      <ProtectedRoute allowedRoles={["scout"]}>
                        <Layout>
                          <ScoutTalentSearch />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/reports"
                    element={
                      <ProtectedRoute allowedRoles={["scout"]}>
                        <Layout>
                          <ScoutReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/recommendations"
                    element={
                      <ProtectedRoute allowedRoles={["scout"]}>
                        <Layout>
                          <ScoutRecommendations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/trial-tracking"
                    element={
                      <ProtectedRoute allowedRoles={["scout"]}>
                        <Layout>
                          <TrialTracking />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scout/reports/new"
                    element={<Navigate to="/scout/reports" replace />}
                  />
                  <Route
                    path="/owner/scouting-reports"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner"]}>
                        <Layout>
                          <OwnerScoutingReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/recommendations"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <CoachRecommendations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/assignments"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeAssignments />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/result-entry"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeResultEntry />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/profile"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeProfile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/open-games"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeOpenGames />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referee/discipline"
                    element={
                      <ProtectedRoute allowedRoles={["referee"]}>
                        <Layout>
                          <RefereeDiscipline />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/medical/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["medical_staff"]}>
                        <Layout>
                          <MedicalDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/medical/injuries"
                    element={
                      <ProtectedRoute allowedRoles={["medical_staff"]}>
                        <Layout>
                          <MedicalInjuries />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/medical/welfare"
                    element={
                      <ProtectedRoute allowedRoles={["medical_staff"]}>
                        <Layout>
                          <MedicWelfare />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/medical/clearances"
                    element={
                      <ProtectedRoute allowedRoles={["medical_staff", "coach", "parent", "club_owner"]}>
                        <Layout>
                          <MedicalClearances />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/medical/analytics"
                    element={
                      <ProtectedRoute allowedRoles={["medical_staff", "coach", "club_owner", "team_manager"]}>
                        <Layout>
                          <InjuryAnalytics />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/welfare/staff"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner", "club_admin"]}>
                        <Layout>
                          <StaffAssignments />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venue/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["venue_owner"]}>
                        <Layout>
                          <VenueDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venue/my-venue"
                    element={
                      <ProtectedRoute allowedRoles={["venue_owner"]}>
                        <Layout>
                          <MyVenue />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venue/:venueId"
                    element={
                      <Layout>
                        <VenueDetails />
                      </Layout>
                    }
                  />
                  <Route
                    path="/venue/bookings"
                    element={
                      <ProtectedRoute allowedRoles={["venue_owner"]}>
                        <Layout>
                          <VenueBookings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venue/pricing"
                    element={
                      <ProtectedRoute allowedRoles={["venue_owner"]}>
                        <Layout>
                          <VenuePricing />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venue/insights"
                    element={
                      <ProtectedRoute allowedRoles={["venue_owner"]}>
                        <Layout>
                          <VenueInsights />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <OrganizerDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/tournament"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <Tournaments />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/tournament/:tournamentId"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <TournamentDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/ops/:tournamentId"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <TournamentOps />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/tournament/participants"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <TournamentRegistrations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/schedule"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <OrganizerSchedule />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/check-in"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer", "admin", "super_admin"]}>
                        <Layout>
                          <EventCheckIn />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizer/brackets"
                    element={
                      <ProtectedRoute allowedRoles={["tournament_organizer"]}>
                        <Layout>
                          <OrganizerBrackets />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Media Manager Routes (formerly Team Manager) */}
                  <Route
                    path="/media-manager/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/posts"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <PostsManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/analytics"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <MediaManagerAnalytics />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/club-page"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerClubPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/media"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerMedia />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/productivity"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerProductivity />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/match-coordination"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerMatchCoordination />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/media-manager/reports"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Parent */}
                  <Route
                    path="/children"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentChildren />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/household"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentHousehold />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent/welfare"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentWelfare />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/children/player-profile"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ChildrenPlayerProfile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/children/schedule"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentSchedule />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/children/program-fee"
                    element={
                      <ProtectedRoute allowedRoles={["parent"]}>
                        <Layout>
                          <ParentProgramFees />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Team manager pages. /media-manager/* stays as the sidebar alias. */}
                  <Route
                    path="/team-manager/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/club-page"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerClubPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/media"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerMedia />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/productivity"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerProductivity />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/posts"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <PostsManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/analytics"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <MediaManagerAnalytics />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/match-coordination"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerMatchCoordination />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/reports"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <TeamManagerReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/welfare"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager"]}>
                        <Layout>
                          <CoachWelfare />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <AdminDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/approval-center"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <ApprovalCenter />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/national-id"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <NationalIDVerification />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-management"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <UserManagementV2 />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner", "parent", "venue_owner", "coach"]}>
                        <Layout>
                          <OnboardingPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/import"
                    element={
                      <ProtectedRoute allowedRoles={["club_owner", "club_admin"]}>
                        <Layout>
                          <OnboardingPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/coach-licence"
                    element={
                      <ProtectedRoute allowedRoles={["coach"]}>
                        <Layout>
                          <OnboardingPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/consents"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PrivacyCenter />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SearchPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/development"
                    element={
                      <ProtectedRoute allowedRoles={["coach", "parent", "player"]}>
                        <Layout>
                          <PlayerDevelopment />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team-manager/volunteers"
                    element={
                      <ProtectedRoute allowedRoles={["team_manager", "club_owner", "club_admin"]}>
                        <Layout>
                          <TeamOps />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/safeguarding"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <SafeguardingAdmin />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/pdpa"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <PdpaAdmin />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ops"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <OpsKpis />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/moderation"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <Moderation />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/system-reports"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <SystemReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/feedback"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <AdminFeedback />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/parent-management"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <ParentAccountManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/super/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <SuperAdminDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/super/announcement"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <AdminAnnouncement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/blog"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                        <Layout>
                          <BlogManager />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/blog/new"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                        <Layout>
                          <EditPost />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/blog/edit/:id"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
                        <Layout>
                          <EditPost />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <SubscriptionDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/plans"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <SubscriptionPlans />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/active"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <ActiveSubscriptions />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/transactions"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <BillingTransactions />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/promos"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <PromoCodes />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/settings"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <MonetizationSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/subscriptions/analytics"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <UsageAnalytics />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {import.meta.env.DEV && PageValidator ? (
                  <Route
                    path="/super/page-validator"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <PageValidator />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  ) : null}
                  <Route
                    path="/super/content"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <ContentManager />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/content/footer"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <FooterManager />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/content/email-templates"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <EmailTemplates />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/content/translations"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <TranslationsManager />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/feature-flags"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <FeatureFlags />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/monitoring"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <Monitoring />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/system-settings"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <SystemSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/admin-management"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <AdminManagement />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/roles-permissions"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <RolesPermissions />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/ai-control"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <AIControl />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/audit-logs"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <AuditLogs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/financial"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <Financial />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  {import.meta.env.DEV && IntegrationTest ? (
                  <Route
                    path="/super/integration-test"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <IntegrationTest />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  ) : null}
                  <Route
                    path="/super/whatsapp-settings"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <WhatsAppSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/integrations"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <Layout>
                          <IntegrationsConfig />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super/refunds"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <Layout>
                          <RefundCenter />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Team Routes */}
                  <Route
                    path="/team/create"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CreateTeam />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/my-teams"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MyTeams />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/:teamId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TeamDetail />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Messages />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Profile />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/role-request"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <RoleRequest />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/social"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "fan",
                          "player",
                          "parent",
                          "coach",
                          "club_owner",
                          "club_admin",
                          "club_manager",
                          "club_finance",
                          "scout",
                          "scouter",
                          "referee",
                          "medical_staff",
                          "venue_owner",
                          "tournament_organizer",
                          "team_manager",
                          "admin",
                          "super_admin",
                        ]}
                      >
                        <Layout>
                          <Social />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/jobs"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "coach",
                          "scout",
                          "club_owner",
                          "medical_staff",
                          "referee",
                        ]}
                      >
                        <Layout>
                          <Jobs />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/ops/*"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <BatchCOps />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
                </ShellProvider>
              </Suspense>
            </ResponsiveAppWrapper>
          </TooltipProvider>
        </ResponsiveProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
